import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Smartphone, Mail, MapPin, Globe, Send, Loader2 } from "lucide-react";
import { PageHero } from "@/components/sections";
import MapEmbed from "@/components/MapEmbed";
import { Reveal } from "@/components/primitives";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { IUBENDA_PRIVACY_URL } from "@/lib/iubenda";
import { CONTACT, CONTACT_REASONS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { nome_cognome: "", telefono: "", email: "", motivo: "", messaggio: "", privacy: false, website: "" };

const Contatti = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome_cognome || !form.telefono || !form.email || !form.motivo || !form.messaggio) {
      toast.error("Compila tutti i campi per inviare la richiesta.");
      return;
    }
    if (!form.privacy) {
      toast.error("Devi accettare la Privacy Policy per inviare la richiesta.");
      return;
    }
    setLoading(true);
    try {
      const { privacy, ...rest } = form;
      await axios.post(`${API}/contact`, { ...rest, privacy_accettata: privacy, origine: "Modulo Contatti", pagina: window.location.pathname });
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setForm(initial);
    } catch (err) {
      toast.error("Si è verificato un errore. Riprova o chiamaci direttamente.");
    } finally {
      setLoading(false);
    }
  };

  const infos = [
    { icon: MapPin, label: "Indirizzo", value: CONTACT.address },
    { icon: Phone, label: "Telefono", value: `${CONTACT.tel1} · ${CONTACT.tel2}` },
    { icon: Smartphone, label: "Mobile", value: CONTACT.mobile },
    { icon: Mail, label: "Email", value: CONTACT.email },
    { icon: Globe, label: "Sito web", value: CONTACT.web },
  ];

  return (
    <>
      <PageHero
        eyebrow="Contatti"
        title={"Parliamo\ndelle tue gambe."}
        subtitle="Prenota la tua misurazione o richiedi informazioni. Siamo a Napoli, in Via Giuseppe Bonito 19."
        testid="contatti-hero"
      />

      <section className="py-20 lg:py-28 bg-white" data-testid="contatti-body">
        <div className="container-x grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16">
          {/* Info */}
          <div>
            <span className="eyebrow">{CONTACT.company}</span>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-serif text-3xl lg:text-4xl font-bold text-navy">Dove trovarci.</h2>
            </Reveal>
            <div className="mt-8 space-y-5">
              {infos.map((it) => (
                <div key={it.label} className="flex items-start gap-4" data-testid={`contact-info-${it.label.toLowerCase()}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green/10 text-green"><it.icon size={19} /></span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{it.label}</div>
                    <div className="mt-0.5 font-medium text-navy">{it.value}</div>
                  </div>
                </div>
              ))}
            </div>

            <MapEmbed />
          </div>

          {/* Form */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-3xl border border-[#E7EDF2] bg-cloud/60 p-8 lg:p-10" data-testid="contact-form">
              <h3 className="font-serif text-2xl font-bold text-navy">Scrivici</h3>
              <p className="mt-2 text-sm text-muted-foreground">Compila il modulo, ti ricontatteremo al più presto.</p>

              <div className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="nome" className="text-navy">Nome e Cognome</Label>
                  <Input id="nome" data-testid="input-nome" value={form.nome_cognome} onChange={set("nome_cognome")} placeholder="Mario Rossi" className="mt-2 bg-white h-12" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="tel" className="text-navy">Telefono</Label>
                    <Input id="tel" data-testid="input-telefono" value={form.telefono} onChange={set("telefono")} placeholder="339 1424138" className="mt-2 bg-white h-12" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-navy">Email</Label>
                    <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} placeholder="nome@email.it" className="mt-2 bg-white h-12" />
                  </div>
                </div>
                <div>
                  <Label className="text-navy">Motivo del contatto</Label>
                  <Select value={form.motivo} onValueChange={set("motivo")}>
                    <SelectTrigger data-testid="select-motivo" className="mt-2 bg-white h-12">
                      <SelectValue placeholder="Seleziona un motivo" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_REASONS.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`motivo-${r.toLowerCase().replace(/\s+/g, "-")}`}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="msg" className="text-navy">Messaggio</Label>
                  <Textarea id="msg" data-testid="input-messaggio" value={form.messaggio} onChange={set("messaggio")} placeholder="Raccontaci come possiamo aiutarti…" rows={5} className="mt-2 bg-white resize-none" />
                </div>
                {/* Honeypot anti-spam: invisibile agli utenti */}
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} aria-hidden="true" className="hidden" />
                <p className="text-xs text-muted-foreground leading-relaxed" data-testid="contact-health-notice">
                  Non inserire diagnosi, patologie, prescrizioni, referti o altre informazioni relative alla salute. Il modulo è destinato esclusivamente alla richiesta di contatto e alla prenotazione del servizio.
                </p>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy"
                    checked={form.privacy}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, privacy: v === true }))}
                    data-testid="contact-privacy-checkbox"
                    className="mt-0.5 border-navy/30 data-[state=checked]:bg-green data-[state=checked]:border-green"
                  />
                  <Label htmlFor="privacy" className="text-sm font-normal leading-relaxed text-navy/80 cursor-pointer">
                    Dichiaro di aver letto l&apos;{" "}
                    <a href={IUBENDA_PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-green underline underline-offset-2" data-testid="contact-privacy-link">
                      Informativa Privacy
                    </a>.
                  </Label>
                </div>
                <button type="submit" data-testid="contact-submit-button" disabled={loading || !form.privacy} className="btn-green w-full disabled:opacity-60 disabled:cursor-not-allowed">
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Invio in corso…</> : <>Invia richiesta <Send size={16} /></>}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contatti;
